import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, AlertCircle } from 'lucide-react';

interface AIVisualProps {
  src: string;
  alt: string;
  className?: string;
  fallbackType?: 'health' | 'food' | 'tech' | 'abstract';
  aspectRatio?: 'video' | 'square' | 'portrait';
}

const FALLBACK_IMAGES = {
  health: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&h=675&fit=crop',
  food: 'https://images.unsplash.com/photo-1547592166-23ac45744acd?w=1200&h=675&fit=crop',
  tech: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1200&h=675&fit=crop',
  abstract: 'https://images.unsplash.com/photo-1557683316-973673baf926?w=1200&h=675&fit=crop'
};

export const AIVisual: React.FC<AIVisualProps> = ({
  src,
  alt,
  className = '',
  fallbackType = 'abstract',
  aspectRatio = 'video'
}) => {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [currentSrc, setCurrentSrc] = useState(src);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setStatus('loading');
    setCurrentSrc(src);
    setRetryCount(0);
  }, [src]);

  const handleLoad = () => {
    setStatus('loaded');
  };

  const handleError = () => {
    if (retryCount === 0) {
      // First error: try the Unsplash fallback
      setRetryCount(1);
      setCurrentSrc(FALLBACK_IMAGES[fallbackType]);
    } else {
      // Second error: show error state or gradient
      setStatus('error');
    }
  };

  const aspectClass = {
    video: 'aspect-video',
    square: 'aspect-square',
    portrait: 'aspect-[3/4]'
  }[aspectRatio];

  return (
    <div className={`relative overflow-hidden ${aspectClass} ${className} bg-theme-surface rounded-xl border border-theme-border/20`}>
      <AnimatePresence mode="wait">
        {status === 'loading' && (
          <motion.div
            key="loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex flex-col items-center justify-center bg-theme-surface z-10"
          >
            <Loader2 className="w-8 h-8 text-theme-primary animate-spin mb-2" />
            <span className="text-[10px] uppercase tracking-widest text-theme-secondary font-bold">Synthesizing Visual...</span>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            key="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-theme-surface to-theme-elevated p-4 text-center"
          >
            <AlertCircle className="w-8 h-8 text-theme-primary opacity-50 mb-2" />
            <p className="text-xs text-theme-secondary font-medium">{alt}</p>
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,var(--tw-gradient-from),transparent)]" />
          </motion.div>
        )}
      </AnimatePresence>

      <img
        src={currentSrc}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-700 ${status === 'loaded' ? 'opacity-100' : 'opacity-0'}`}
        onLoad={handleLoad}
        onError={handleError}
      />
      
      {/* Premium Overlay */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-60" />
    </div>
  );
};

export default AIVisual;
